import { useRef, useState } from 'react';

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  IconButton,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';

import { DeleteOutline, useToast } from '@aslanpay/portal-common-lib';

import useDeleteSavingsPresetImage from 'app/hooks/useDeleteSavingsPresetImage';

import CategoryImageLightbox from './CategoryImageLightbox';
import PresetThumbnail from './PresetThumbnail';
import { PresetImageOption, UnusedPresetImagesProps } from './types';

const THUMBNAIL_SIZE = '72px';

/**
 * Lists preset images not used by any global category, so an admin can delete them from storage.
 *
 * Note: usage is only checked against the global categories loaded here — it does not detect images
 * still referenced by company-specific (e.g. cloned) categories, hence the warning and confirmation.
 */
function UnusedPresetImages({ images, categories, onAssign }: UnusedPresetImagesProps) {
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [imageToDelete, setImageToDelete] = useState<PresetImageOption | undefined>(undefined);
  const [lightboxIndex, setLightboxIndex] = useState<number | undefined>(undefined);

  const deletePreset = useDeleteSavingsPresetImage({
    onSuccess: () =>
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Deleted',
        description: 'The image was deleted from storage.',
      }),
    onError: () =>
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Delete failed',
        description: 'The image could not be deleted. Please try again.',
      }),
  });

  return (
    <Box borderTop="1px solid" borderColor="brand.lightGray1" pt="20px">
      <Text textStyle="smallSemiBold" color="brand.darkPurple" mb="4px">
        Unused images ({images.length})
      </Text>
      <Text textStyle="extraSmallRegular" color="brand.gray" mb="12px">
        These images aren&apos;t used by any global category. Deleting removes an image from storage
        permanently — this does not detect images still used by company-specific categories.
      </Text>

      <SimpleGrid columns={{ base: 4, sm: 6, md: 8 }} spacing="12px">
        {images.map((image, index) => (
          <Box
            key={image.id}
            position="relative"
            w={THUMBNAIL_SIZE}
            data-testid="unused-preset-image"
          >
            <Box
              as="button"
              type="button"
              display="block"
              w="full"
              borderRadius="8px"
              cursor="pointer"
              aria-label={`View ${image.name}`}
              onClick={() => setLightboxIndex(index)}
            >
              <PresetThumbnail imageId={image.id} fileName={image.name} size={THUMBNAIL_SIZE} />
            </Box>
            <IconButton
              aria-label={`Delete ${image.name}`}
              icon={<DeleteOutline boxSize="14px" />}
              size="xs"
              position="absolute"
              top="-8px"
              right="-8px"
              borderRadius="full"
              colorScheme="red"
              onClick={() => setImageToDelete(image)}
            />
          </Box>
        ))}
      </SimpleGrid>

      <AlertDialog
        isOpen={!!imageToDelete}
        leastDestructiveRef={cancelRef}
        onClose={() => setImageToDelete(undefined)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent data-testid="delete-unused-image-dialog">
            <AlertDialogHeader>Delete image</AlertDialogHeader>
            <AlertDialogBody>
              Permanently delete this image from storage? This cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="outline" onClick={() => setImageToDelete(undefined)}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                ml="12px"
                isLoading={deletePreset.isLoading}
                onClick={() => {
                  if (imageToDelete) {
                    deletePreset.mutate(imageToDelete.id);
                  }
                  setImageToDelete(undefined);
                }}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {lightboxIndex !== undefined && (
        <CategoryImageLightbox
          isOpen
          images={images.map((image) => ({ imageId: image.id, fileName: image.name }))}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(undefined)}
          assignableCategories={categories}
          onAssign={onAssign}
        />
      )}
    </Box>
  );
}

export default UnusedPresetImages;
